// ThemeContext.js
// -----------------------------------------------------------------------------
// Gestione del tema (dark / light) per le dashboard live.
//
// Il tema viene applicato a livello di <html> tramite l'attributo
// `data-theme="light"` o `data-theme="dark"`. I file CSS definiscono un set
// di variabili CSS che cambiano in base al tema: in questo modo un singolo
// interruttore ridipinge tutta la UI senza re-render di componenti React.
//
// Il canvas della mappa (che disegna pixel a mano e non riceve stili CSS)
// legge le stesse variabili tramite getComputedStyle(). Per farlo ri-leggere
// i colori al volo, esponiamo lo stato `theme` via hook: i componenti che
// disegnano il canvas possono metterlo nella dependency list dell'effect.
//
// Persistenza:
//   - localStorage key:  "racesense-theme"  → "light" | "dark"
//   - Fallback iniziale: window.matchMedia('(prefers-color-scheme: light)')
// -----------------------------------------------------------------------------

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const STORAGE_KEY = 'racesense-theme';
const VALID = ['light', 'dark'];

const ThemeContext = createContext({
    theme: 'dark',
    setTheme: () => { },
    toggleTheme: () => { },
});

function detectInitialTheme() {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved && VALID.includes(saved)) return saved;
    } catch { /* storage non disponibile */ }

    // Fallback: preferenza di sistema (iOS/Android/OSX)
    try {
        if (typeof window !== 'undefined' && window.matchMedia) {
            if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
        }
    } catch { /* ignora */ }

    return 'dark';
}

function applyThemeToDocument(theme) {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', theme);
    // color-scheme aiuta il browser a colorare scrollbar, input, ecc.
    document.documentElement.style.colorScheme = theme;
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(() => detectInitialTheme());

    // Applica subito il tema al documento al primo mount.
    useEffect(() => {
        applyThemeToDocument(theme);
    }, [theme]);

    // Ascolta il cambio di preferenza di sistema SOLO se l'utente non ha mai
    // scelto manualmente (cioè non c'è niente in localStorage).
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        let saved = null;
        try { saved = localStorage.getItem(STORAGE_KEY); } catch { }
        if (saved) return; // scelta manuale: non seguire il sistema
        const mql = window.matchMedia('(prefers-color-scheme: light)');
        const onChange = (e) => setThemeState(e.matches ? 'light' : 'dark');
        try { mql.addEventListener('change', onChange); }
        catch { mql.addListener(onChange); /* Safari vecchio */ }
        return () => {
            try { mql.removeEventListener('change', onChange); }
            catch { mql.removeListener(onChange); }
        };
    }, []);

    const setTheme = useCallback((next) => {
        if (!VALID.includes(next)) return;
        setThemeState(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch { }
    }, []);

    const toggleTheme = useCallback(() => {
        setThemeState(prev => {
            const next = prev === 'dark' ? 'light' : 'dark';
            try { localStorage.setItem(STORAGE_KEY, next); } catch { }
            return next;
        });
    }, []);

    const value = useMemo(
        () => ({ theme, setTheme, toggleTheme }),
        [theme, setTheme, toggleTheme]
    );

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}

// -----------------------------------------------------------------------------
// ThemeToggle — bottone elegante sole/luna da inserire nelle top bar delle
// dashboard live. Si occupa solo di visualizzarsi e cambiare tema; la
// grafica (colori, bordi) è definita in App.css via la classe `.theme-toggle`.
// -----------------------------------------------------------------------------
export function ThemeToggle({ className = '', title }) {
    const { theme, toggleTheme } = useTheme();
    const isDark = theme === 'dark';
    const label = title || (isDark ? 'Passa al tema chiaro' : 'Passa al tema scuro');

    return (
        <button
            type="button"
            className={`theme-toggle ${className}`.trim()}
            onClick={toggleTheme}
            aria-label={label}
            title={label}
        >
            <span className="theme-toggle-track" aria-hidden="true">
                <span className="theme-toggle-thumb">
                    {isDark ? (
                        // Luna
                        <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16.5 12.5A7 7 0 0 1 7.5 3.5a7 7 0 1 0 9 9Z" />
                        </svg>
                    ) : (
                        // Sole
                        <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="10" cy="10" r="3.2" />
                            <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.5 4.5l1.4 1.4M14.1 14.1l1.4 1.4M4.5 15.5l1.4-1.4M14.1 5.9l1.4-1.4" />
                        </svg>
                    )}
                </span>
            </span>
        </button>
    );
}
